import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExceptionVideoPreview } from "components/locationExceptionalStatus/ExceptionVideoPreview";
import { SafeDateTimeRangePicker } from "components/SafeDateTimeRangePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { getExceptionalStatusTranslationKey } from "src/utils/getExceptionalStatusTranslationKey";
import { useEffect, useState } from "react";
import { LocationExceptionalStatusType } from "src/store/api/generatedApi";
import {
  EMPTY_DRAFT,
  ExceptionDraft,
  useExceptionDraft,
} from "../contexts/ExceptionDraftContext";
import { useOverlappingExceptions } from "./useDayExceptions";

dayjs.extend(utc);

const STATUS_OPTIONS: LocationExceptionalStatusType[] = [
  "ignorable",
  "missing",
  "bad_labels",
];

const PICKER_FORMAT = "MM-DD-YYYY HH:mm:ss";
const TIME_FORMAT = "MM-DD HH:mm:ss";

export const ExceptionDraftDialog = () => {
  const { t } = useTenantTranslation();
  const {
    location,
    exceptionalStatuses,
    mode,
    editingException,
    draft,
    patchDraft,
    closeDialog,
    submit,
    isSubmitting,
    submitError,
    pendingDeleteId,
    cancelDelete,
    confirmDelete,
  } = useExceptionDraft();

  const isEdit = mode === "edit";
  const [editForm, setEditForm] = useState<ExceptionDraft>(EMPTY_DRAFT);

  useEffect(() => {
    if (mode === "edit" && editingException !== null) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- re-seed when a different row is opened
      setEditForm({
        status: editingException.status,
        startedAt: dayjs.utc(editingException.started_at),
        endedAt: dayjs.utc(editingException.ended_at),
        note: editingException.note ?? "",
      });
    }
  }, [mode, editingException]);

  const form = isEdit ? editForm : draft;
  const setField = (patch: Partial<ExceptionDraft>) => {
    if (isEdit) {
      setEditForm((previous) => ({ ...previous, ...patch }));
    } else {
      patchDraft(patch);
    }
  };

  const overlapping = useOverlappingExceptions({
    exceptionalStatuses,
    startedAt: form.startedAt,
    endedAt: form.endedAt,
    excludeId: isEdit ? editingException?.id : undefined,
  });
  const sameStatusOverlap = overlapping.filter(
    (status) => status.status === form.status
  );

  const hasValidRange =
    form.startedAt !== null &&
    form.endedAt !== null &&
    form.startedAt.isBefore(form.endedAt);
  const canSubmit = hasValidRange && sameStatusOverlap.length === 0;

  return (
    <>
      <Dialog
        open={mode !== null}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isEdit ? t("editExceptionalPeriod") : t("addExceptionalPeriod")}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
            <Stack spacing={2} sx={{ width: 400, flexShrink: 0 }}>
              {isEdit ? (
                <SafeDateTimeRangePicker
                  value={[form.startedAt, form.endedAt]}
                  onChange={(range) => {
                    setField({ startedAt: range[0], endedAt: range[1] });
                  }}
                  ampm={false}
                  format={PICKER_FORMAT}
                  timezone="UTC"
                />
              ) : form.startedAt !== null && form.endedAt !== null ? (
                <Typography variant="body2" color="text.secondary">
                  {form.startedAt.utc().format(PICKER_FORMAT)}
                  {" – "}
                  {form.endedAt.utc().format(PICKER_FORMAT)} UTC
                </Typography>
              ) : null}

              <RadioGroup
                row
                value={form.status}
                onChange={(event) => {
                  setField({
                    status: event.target.value as LocationExceptionalStatusType,
                  });
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={t(getExceptionalStatusTranslationKey(option))}
                  />
                ))}
              </RadioGroup>

              <TextField
                label={t("note")}
                value={form.note}
                onChange={(event) => {
                  setField({ note: event.target.value });
                }}
                multiline
                rows={2}
              />

              {sameStatusOverlap.length > 0 ? (
                <Alert severity="warning">
                  <Typography variant="body2">
                    {t("exceptionOverlapsSameStatus", {
                      status: t(
                        getExceptionalStatusTranslationKey(form.status)
                      ),
                    })}
                  </Typography>
                  {sameStatusOverlap.map((status) => (
                    <Typography
                      key={status.id}
                      variant="caption"
                      display="block"
                    >
                      {dayjs.utc(status.started_at).format(TIME_FORMAT)} –{" "}
                      {dayjs.utc(status.ended_at).format(TIME_FORMAT)}
                    </Typography>
                  ))}
                </Alert>
              ) : null}

              {submitError === "overlap" ? (
                <Alert severity="error">
                  {t("exceptionOverlapsSubmitFailed")}
                </Alert>
              ) : submitError === "unknown" ? (
                <Alert severity="error">{t("unknownError")}</Alert>
              ) : null}
            </Stack>

            <ExceptionVideoPreview
              location={location}
              startedAt={form.startedAt}
              endedAt={form.endedAt}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t("cancel")}</Button>
          <Button
            variant="contained"
            disabled={!canSubmit || isSubmitting}
            onClick={() => void submit(form)}
          >
            {isEdit ? t("save") : t("create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingDeleteId !== null} onClose={cancelDelete}>
        <DialogTitle>{t("deleteExceptionalPeriod")}</DialogTitle>
        <DialogContent>{t("confirmDelete")}</DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>{t("cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void confirmDelete()}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
